/**
 * Раскладка вкладки «Мониторинг». Всё, что есть в глобальном base.css (.kpi, .tbl, .tblwrap,
 * .card-flat, .val, .dot, .seg), здесь НЕ переопределяется — соответствие макету
 * Monitoring.html проверяется сверкой классов, а не чтением двух файлов сразу.
 *
 * Тут сетка страницы и то, чего в дизайн-системе пока нет ровно потому, что нужно только
 * этому экрану: сворачиваемый раздел (заголовок-кнопка, бейдж, тело) и сетка живости
 * источников. Появится второй потребитель — переедет в base.css целиком, а не копией.
 */

/* Страница — флекс-ребёнок каркаса Shell, поэтому скролл держит она, а не документ. */
.page {
  /* Дашборд не растягивается во всю ширину монитора: строка таблицы на 1900px заставляет
     глаз ехать от края до края, а связь «подпись — значение» теряется. Предел один на три
     экрана, поэтому живёт токеном в tokens.css, а не тремя числами врозь. Экран черновика
     этот предел НЕ делит — у него свой (`--workspace-max`) и своё обоснование.
     Поля тоже токенами: на телефоне 32px с каждой стороны съедают шестую часть экрана. */
  width: 100%;
  max-width: var(--surface-max);
  margin-inline: auto;
  flex: 1;
  min-width: 0;
  padding: var(--page-pad-y) var(--page-pad-x);
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
}

.head {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}

.headText {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Сетка KPI. minmax(0,1fr) — иначе длинное значение растягивает колонку. */
.kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
.kpis3 {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sectionHead {
  display: flex;
  align-items: center;
  gap: 10px;
}

/*
 * ══ сворачиваемый раздел ═══════════════════════════════════════════════════
 *
 * Заголовок — кнопка во всю ширину, обнулённая до строки: своей рамки и фона у неё нет,
 * потому что она не элемент управления рядом с блоком, а сам заголовок блока. Признак
 * нажимаемости — подложка на наведении, а не рамка: рамка сдвинула бы содержимое на пиксель,
 * и заголовок «дёргался» бы под курсором (та же причина, что у карточки метрики ниже).
 */
.sectionToggle {
  display: flex;
  /* Узкий экран (≤ 414 px): заголовок, бейдж без переноса и подпись источника данных в одну
   * строку не помещаются, и кнопка выпирала за страницу на 34 px — страница ездила вбок.
   * Перенос отправляет подпись на вторую строку. */
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px 8px;
  margin-left: -8px;
  border: none;
  background: none;
  font: inherit;
  color: var(--ink-2);
  text-align: left;
  cursor: pointer;
  border-radius: var(--r-ctl);
  transition: background 160ms var(--ease);
}
.sectionToggle:hover {
  background: var(--violet-bg);
}
.sectionToggle:focus-visible {
  outline: 2px solid var(--violet);
  outline-offset: 2px;
}

/* Ровная строка вместо заголовка: разворачивать нечего. Высота — та же, что у заголовка. */
.calmRow {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 6px 8px;
  margin-left: -8px;
  min-height: 36px;
}

/*
 * Бейдж заголовка — единственное, что видно из свёрнутого раздела помимо названия, поэтому
 * он обязан читаться и в спокойном виде (счётчик), и в тревожном (авария). Цвет — токенами.
 */
.badge {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 2px 8px;
  border-radius: var(--r-chip);
  background: var(--neutral-bg);
  color: var(--ink-2);
  white-space: nowrap;
}
.badgeAlarm {
  background: var(--red-bg);
  color: var(--red-ink);
}

/*
 * Тело раздела не размонтируется, а получает `hidden`: состояние внутри (выбранный источник,
 * выбранный разрез расхода) переживает сворачивание, а данные грузит страница, и прятать их
 * из дерева нечего ради. `display` у класса свой, поэтому правило UA для `[hidden]` пришлось
 * бы иначе переопределять молча — вот оно явно.
 */
.sectionBody {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.sectionBody[hidden] {
  display: none;
}

/* minmax(0, 1fr), а не 1fr: у `1fr` нижняя граница — min-content, и широкая таблица внутри
   растягивает свою колонку, а вместе с ней и страницу. */
.two {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}

/* Числовая ячейка таблицы: табличные цифры дают ровную колонку без моноширинной гарнитуры. */
.numCell {
  font-family: var(--sans);
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.codeCell {
  font-family: var(--sans);
  font-size: 13px;
  color: var(--ink);
}

.stateCell {
  display: inline-flex;
  gap: 7px;
  align-items: center;
}

/*
 * Класс ошибки прогона — единственная ячейка ТАБЛИЦ этого экрана, которой разрешено
 * переноситься: строка приходит произвольной длины, а обрезание её многоточием стирает ровно
 * ту часть, ради которой колонка заведена (06-security-ops §6.2: «сорван» без класса ошибки
 * не отличает 404 от бана по диапазону).
 *
 * Полный текст ошибки ИСТОЧНИКА живёт не здесь: у него своя область под сеткой живости
 * (`.srcError`), и она показывает один текст, а не двадцать один.
 */
.errCell {
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.4;
}

/* Две кнопки оценки в строке — не переносятся и не растягивают колонку. */
.markButtons {
  display: flex;
  gap: 8px;
  white-space: nowrap;
}

.tableNote {
  padding: 12px 16px;
  border-top: 1px solid var(--border-2);
}

/*
 * Карточка метрики, открывающая окно тренда. Кнопка обнуляется до обёртки: своей рамки и
 * фона у неё нет — их несёт `.kpi` внутри, и вторая рамка вокруг карточки читалась бы как
 * вложенный блок. Кольцо на наведении и есть единственный признак нажимаемости, поэтому оно
 * рисуется тенью: рамка сдвинула бы содержимое на пиксель и карточка «дёргалась» бы.
 */
.metricButton {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  border-radius: var(--r-card);
  transition: box-shadow 200ms var(--ease);
}
.metricButton:hover {
  box-shadow: 0 0 0 2px var(--violet-bg-2);
}
.metricButton:focus-visible {
  outline: 2px solid var(--violet);
  outline-offset: 2px;
}

/* График растягивается по ширине окна и держит пропорции: подписи под столбцами не должны
   сплющиваться вместе с шириной, иначе неделя читается как «2 08». */
.chart {
  display: block;
  width: 100%;
  height: auto;
}

/* Переключатель диапазона в плавающем окне. Погашённая кнопка остаётся видимой: спрятанная
   читалась бы как «такой возможности нет», а её просто не на чем пока применить. */
.ranges {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
  padding: 0;
  border: none;
  flex-wrap: wrap;
}
.rangesLegend {
  padding: 0;
  margin-right: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}
.rangeButton {
  padding: 6px 12px;
  border: 1px solid var(--border);
  border-radius: var(--r-btn);
  background: var(--white);
  color: var(--ink-2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  transition:
    background 160ms var(--ease),
    color 160ms var(--ease);
}
.rangeButton:hover:not(:disabled) {
  background: var(--violet-bg-2);
}
.rangeButton[aria-pressed="true"] {
  background: var(--violet);
  border-color: var(--violet);
  color: var(--on-accent);
}
.rangeButton:disabled {
  opacity: 0.45;
  cursor: default;
}
.rangeButton:focus-visible {
  outline: 2px solid var(--violet);
  outline-offset: 2px;
}

/*
 * ══ живость источников: сетка ячеек ════════════════════════════════════════
 *
 * Двадцать одна строка таблицы в семь колонок отвечала на вопрос «что сломалось» экраном
 * текста. Ячейка отвечает на него двумя значениями — код и доля успехов, — а остальное
 * раскрывается по нажатию для ОДНОГО источника.
 */
.srcGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
}

/*
 * ЦВЕТ — ЛЕВОЙ ГРАНИЦЕЙ И МЯГКОЙ ПОДЛОЖКОЙ, НЕ ЗАЛИВКОЙ. Стена сплошного красного нечитаема:
 * на ней перестаёт быть виден единственный мёртвый источник среди пяти деградирующих, а
 * именно его и чинят первым. Все цвета — токены (ui-design §2), фирменных литералов нет.
 *
 * Состояние НЕ кодируется одним цветом: слово состояния стоит в `title` и в скрытом тексте
 * внутри ячейки (`.srOnly`). Цвет здесь — ускорение чтения, а не носитель смысла.
 */
.srcCell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-left: 3px solid var(--muted);
  border-radius: var(--r-ctl);
  background: var(--white);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 160ms var(--ease);
}
.srcCell:hover {
  background: var(--bg-alt);
}
.srcCell:focus-visible {
  outline: 2px solid var(--violet);
  outline-offset: 2px;
}
.srcCell[data-state="ok"] {
  border-left-color: var(--green);
  background: var(--green-bg);
}
.srcCell[data-state="degraded"] {
  border-left-color: var(--amber);
  background: var(--amber-bg);
}
.srcCell[data-state="dead"] {
  border-left-color: var(--red);
  background: var(--red-bg);
}
.srcCell[data-state="unknown"] {
  border-left-color: var(--border);
  background: var(--bg-alt);
}
.srcCell[aria-haspopup="dialog"]:active {
  box-shadow: 0 0 0 2px var(--violet-bg-2);
}
/*
 * Выключенный источник (реестром или из интерфейса): пунктирная рамка и приглушённый текст,
 * подложка — нейтральная, а не цвет здоровья. Мёртвый, но выключенный источник чинить не
 * нужно, и красная подложка звала бы чинить. Слово «выкл.» стоит в ячейке текстом.
 */
.srcCell[data-enabled="false"] {
  border-style: dashed;
  border-left-style: solid;
  border-left-color: var(--border);
  background: var(--white);
  color: var(--muted);
}

.srcCode {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.srcRatio {
  font-size: 11.5px;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
}
.srcWeight {
  font-size: 11.5px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
/* Код выключенного источника — темнее его подписей, но светлее кода включённого. */
.srcCell[data-enabled="false"] .srcCode {
  color: var(--ink-2);
}

/*
 * Скрытый визуально, но не для скринридера. Собственный класс, а не общий: в base.css такого
 * нет, а править base.css в этой правке нельзя — он общий с соседними экранами.
 */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* Подробность выбранного источника — в панели источника (`SourcePanel.tsx`), ОДНА на экран. */
.srcFacts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
}
.srcLog {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/*
 * Полный текст последней ошибки. Переносится и не обрезается: по runbook'у класс ошибки —
 * первый шаг починки мёртвого источника, а обрезанный многоточием стектрейс отвечает на
 * вопрос ровно наполовину. На экране он теперь один, поэтому места ему хватает.
 */
.srcError {
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--red-ink);
  background: var(--red-bg);
  border-radius: var(--r-ctl);
  padding: 8px 10px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* Легенда исходов: код слева, фраза и действие оператора справа. */
.legend {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 12px 16px;
  border-top: 1px solid var(--border-2);
}
.legendRow {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;
}
.legendRow dd {
  margin: 0;
}

@media (max-width: 1280px) {
  .kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .two {
    grid-template-columns: minmax(0, 1fr);
  }
  .legendRow {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
}

/*
 * Телефон (640). Обе сетки KPI — в одну колонку. У `.kpis3` брейкпоинта не было ни одного:
 * три карточки воронки обхода делили 366px, то есть по 122px на карточку с подписью
 * «ОТБРОШЕНО ПРЕФИЛЬТРОМ» в четыре строки и значением под ней. Величина KPI при этом уже
 * зажата `clamp(28px, 7vw, 40px)` в base.css, поэтому карточка не растёт вслед за цифрой.
 *
 * ГРАФИК ТРЕНДА НЕ СЖИМАЕТСЯ. Холст зафиксирован на 1120px именно потому, что подписи в нём
 * — текст: на ширине 294px (модалка на 390px за вычетом полей) 11px-подписи осей дали бы
 * ~3px, то есть серую рябь вместо чисел. Вместо сжатия он держит нижнюю ширину и уезжает
 * горизонтально внутри карточки диалога, у которой в base.css на ≤640 объявлен
 * `overflow-x: auto`, — тот же приём, что у широких таблиц с их `.tblwrap`.
 * 560px — не вкус: при 26 подписях это ~21px на подпись, ниже они начинают слипаться.
 */
@media (max-width: 640px) {
  .kpis,
  .kpis3 {
    grid-template-columns: minmax(0, 1fr);
  }
  .chart {
    min-width: 560px;
  }
}

/* Подпись источника данных в заголовке раздела — длинное имя вьюхи без пробелов. */
.sectionToggle :global(.meta) {
  min-width: 0;
  overflow-wrap: anywhere;
}
